<template>
  <v-app>
    <v-app-bar :color="titleBgColor" :value="showAppBar" app dark fixed>
      <v-row class="flex-nowrap no-gutters">
        <v-col class="d-flex flex-row align-center justify-start" style="width: fit-content">
          <v-btn v-if="canSignUpCancel" id="signupCancel" class="signup_back_button" @click="dialogState = true">
            <v-icon>chevron_left</v-icon>
            <span v-if="canSignUpCancel" class="signup_back_text" v-text="$t('back')" />
          </v-btn>

          <v-btn
            v-if="showBackBtn && signedUp"
            :color="titleColor"
            :style="[fromInquiryHistory ? { pointerEvents: 'none' } : '']"
            dark
            icon
            style="-webkit-margin-start: -15px; background-color: #ffffff45"
            @click="back()"
          >
            <v-icon v-if="signedUp || (isActivation && fromSignUpBtn)" large>chevron_left</v-icon>
          </v-btn>
        </v-col>

        <v-col id="toolbarTitleRoomNumber" class="d-flex align-center justify-center">
          <v-toolbar-title :style="titleTxtStyle" class="text-center" v-text="title" />
        </v-col>

        <v-col class="d-flex flex-row justify-end" style="width: fit-content">
          <v-btn v-if="isGroup" :style="titleTxtStyle" dark icon @click="openGroupProfile()">
            <v-icon>mdi-settings-outline</v-icon>
          </v-btn>

          <SubscReConnectComponent v-if="!isGuest" />
          <NoticeUnreadComponent :color="titleColor" />

          <v-img
            v-if="showNewIcon"
            class="circle-blinks mt-2 me-1"
            max-height="28"
            max-width="46"
            src="@/framework/assets/new-icon.svg"
          />

          <ConnectivityIndicatorComponent />
        </v-col>
      </v-row>
    </v-app-bar>

    <v-main>
      <RouterView />
    </v-main>

    <v-dialog v-model="dialogState" max-width="400px" min-width="300px" persistent>
      <v-card>
        <v-card-title style="font-size: 16px" v-text="$t('confirm_signup_cancel')" />
        <v-card-actions>
          <v-spacer />
          <KtznAdminButton :isOutlined="true" :label="$t('cancel')" @onClick="dialogState = false" />
          <KtznAdminButton :label="$t('ok')" @onClick="cancelSignUp()" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDialog.opend" :class="[fromInquiryHistory ? { hide: true } : '']" max-width="290">
      <ConfirmationCard
        @completeBySwitchResponder="completeBySwitchingResponder($event)"
        @handleCancel="confirmDialog.opend = false"
        @refuseInquiry="completeByRefusingInquiry()"
      >
        <v-col v-if="forwardInquiryId && forwardInquiryId > 0" style="padding-bottom: 0">
          <v-btn
            :color="'#fc461c'"
            class="inquiryBtn"
            outlined
            @click="cancelForward()"
            v-text="$t('cancel_forwarding')"
          />
        </v-col>
        <v-col style="padding-bottom: 0">
          <v-btn :color="'#fc461c'" class="inquiryBtn" outlined @click="doBack()" v-text="$t('back')" />
        </v-col>
      </ConfirmationCard>
    </v-dialog>

    <v-dialog
      v-model="shouldShowResponderDialog"
      :class="[fromInquiryHistory ? { hide: true } : '']"
      max-width="290"
      scrollable
    >
      <ResponderCard
        :closeChatState="closeChatState"
        :okType="true"
        @handleCancel="shouldShowResponderDialog = false"
      />
    </v-dialog>

    <v-dialog v-model="shouldShowRefuseDialog" :class="[fromInquiryHistory ? { hide: true } : '']" max-width="290">
      <RefuseCard @handleCancel="shouldShowRefuseDialog = false" />
    </v-dialog>
  </v-app>
</template>

<script lang="ts">
import store from "@/store";
import { computed, createComponent, onCreated, value, watch, Wrapper } from "vue-function-api";
import { completeInquiry } from "@/modules/staff/services/responder";
import { ProfileMode } from "../constants/group";
import { ConversationRepository } from "@/modules/staff/repositories/conversationRepository";
import { ErrorService } from "../errors/ErrorService";
import { StaffService } from "@/framework/api/Staff/StaffService";
import { InquiryService } from "../api/Staff/ChatService/InquiryService";
import { i18n } from "../plugins/i18n";
import { ACTIVATION_BOT } from "@/modules/auth/services/UserService";
import { ComponentMutation } from "@/framework/store/component/types";
import { ConversationMutation } from "@/framework/store/conversation/types";
import NoticeUnreadComponent from "@/framework/components/NoticeUnread.vue";
import SubscReConnectComponent from "@/framework/components/SubscReConnect.vue";
import ConfirmationCard from "@/framework/components/ConfirmationCard.vue";
import ResponderCard from "@/framework/components/ResponderCard.vue";
import RefuseCard from "@/framework/components/RefuseCard.vue";
import ConnectivityIndicatorComponent from "@/framework/components/ConnectivityIndicator.vue";
import KtznAdminButton from "@/framework/components/common/KtznAdminButton.vue";

export default createComponent({
  components: {
    NoticeUnreadComponent,
    SubscReConnectComponent,
    ConfirmationCard,
    ResponderCard,
    RefuseCard,
    ConnectivityIndicatorComponent,
    KtznAdminButton,
  },
  setup(props, context) {
    const $router = context.root.$router;
    const $route = context.root.$route;
    const conversationRepository = new ConversationRepository();
    const inquiryService = new InquiryService();
    const errorService = new ErrorService();
    const fromInquiryHistory = $route.query.inquiry_history === "1";

    const title: Wrapper<string> = value("");
    const conversationId: Wrapper<string> = value("");
    const titleBgColor: Wrapper<string> = value("#262D46");
    const titleColor: Wrapper<string> = value("#FFFFFF");
    const titleTxtStyle: Wrapper<string> = value("color: #FFFFFF");
    const closeChatState: Wrapper<string> = value("in_chat");
    const importance: Wrapper<string> = value("");
    const forwardInquiryId: Wrapper<string> = value("");

    const isGroup: Wrapper<boolean> = value(false);
    const showBackBtn: Wrapper<boolean> = value(true);
    const isGuest: Wrapper<boolean> = value(false);
    const isActivation: Wrapper<boolean> = value(true);
    const signedUp: Wrapper<boolean> = value(false);
    const fromSignUpBtn: Wrapper<boolean> = value(false);
    const canSignUpCancel: Wrapper<boolean> = value(false);
    const dialogState: Wrapper<boolean> = value(false);
    const shouldShowCompleteDialog: Wrapper<boolean> = value(false);
    const shouldShowResponderDialog: Wrapper<boolean> = value(false);
    const shouldShowRefuseDialog: Wrapper<boolean> = value(false);
    const showNewIcon: Wrapper<boolean> = value(false);

    const allowedStates = ["chatting_guest", "close_staff", "close_guest"];
    const confirmDialog: Wrapper<{ opend: boolean }> = value({ opend: false });

    const btnText = computed(() => {
      if (isGuest.value) {
        return i18n.t("free_signup");
      } else {
        return i18n.t("signup");
      }
    });

    const makeTitle = (str: string) => {
      const elm = document.getElementById("toolbarTitleRoomNumber");
      if (str && elm && elm.clientWidth && str.length > elm.clientWidth / 11) {
        const charLength = Math.round(elm.clientWidth / 11);
        return (
          str.substring(0, Math.round((charLength - 3) / 2)) +
          "..." +
          str.substring(str.length - (Math.round((charLength - 3) / 2) + 4), str.length)
        );
      }
      return str;
    };

    const openSignup = () => {
      const signupType = isGroup.value ? "group_signup" : "guest_signup";
      $router.push({
        path: `/${i18n.locale}/start_conversation/${ACTIVATION_BOT}`,
        query: { fromSignUpBtn: signupType },
      });
    };

    const cancelSignUp = async () => {
      console.error("Not implemented yet");
    };

    const openGroupProfile = () => {
      $router.push({
        path: `/${i18n.locale}/group/profile/${conversationId.value}`,
        query: { mode: ProfileMode.Edit as any },
      });
    };

    const back = async () => {
      if (signedUp.value) {
        //check if the inquiry is importance 7, i.e. it's assigned to the current user
        if (
          $route.name &&
          $route.name === "conversation" &&
          parseInt(importance.value) === 7 //a not completed inquiry or handled by other staff
        ) {
          confirmDialog.value = { opend: true }; //indicating a completable inquiry that requires intention confirmation
        } else {
          $router.push(`/${i18n.locale}/conversations`);
        }
      } else if (isActivation.value && fromSignUpBtn.value) {
        $router.back();
      } else {
        $router.push(`/${i18n.locale}/conversations`);
      }
    };

    const doBack = async () => {
      store.commit(ConversationMutation.SET_IMPORTANCE, "");
      $router.push(`/${i18n.locale}/conversations`);
    };

    const showAppBar = computed(() => {
      return store.getters.voiceTalkMode !== "session";
    });

    const showConfirmationDialogs = () => {
      confirmDialog.value.opend = false;
      shouldShowCompleteDialog.value = true;
    };

    const completeBySwitchingResponder = async (chatState: string) => {
      shouldShowCompleteDialog.value = false;
      closeChatState.value = chatState;

      const listRespondersApiRes = await new StaffService().fetchResponders();
      if (listRespondersApiRes.isErr()) {
        return await errorService.handleError(listRespondersApiRes.error);
      }
      const res = listRespondersApiRes.value;
      if (res.length) shouldShowResponderDialog.value = true;
      else {
        const conversationId = $route.params.conversationId;
        if (conversationId) {
          await completeInquiry(conversationId, 0, closeChatState.value);
          confirmDialog.value.opend = false;
        } else {
          console.warn("Conversation ID is null, completion aborted");
        }
      }
    };

    const completeByRefusingInquiry = () => {
      shouldShowCompleteDialog.value = false;
      shouldShowRefuseDialog.value = true;
    };

    const cancelForward = async () => {
      if (!forwardInquiryId.value || parseInt(forwardInquiryId.value) < 0) {
        console.log("invalid inquiry id");
        return;
      }

      const cancelRes = await inquiryService.cancelGuestInquiryForward(parseInt(forwardInquiryId.value));
      if (cancelRes.isErr()) {
        await errorService.handleError(cancelRes.error);
        $router.push(`/${i18n.locale}/conversations`);
        return;
      }

      store.commit(ConversationMutation.SET_FORWARD_INQUIRY_ID, "");
      confirmDialog.value.opend = false;
      // can maybe do this more cleanly with a push and watching the param queries in convo, but watch doesn't seem to catch?
      $router.push({
        path: `/${i18n.locale}/conversation/${$route.params.conversationId}?pageType=guest`,
        query: { pageType: "guest" },
      });
      $router.go(0);
      console.log("転送キャンセル成功");
    };

    const showNewIconHandler = async () => {
      const currentConversationId = store.getters.currentConversationId;

      const unassignedInquiryExistRes = await inquiryService.getUnassignedInquiries();
      if (unassignedInquiryExistRes.isErr()) {
        return await errorService.handleError(unassignedInquiryExistRes.error);
      }
      // New icon should not be displayed if the only new inquiry is conversation that is currently displayed
      if (unassignedInquiryExistRes.value) {
        const newUnAssignedInquiryList = unassignedInquiryExistRes.value.filter(
          (id: string) => id !== currentConversationId
        );
        newUnAssignedInquiryList.length == 0 ? (showNewIcon.value = false) : (showNewIcon.value = true);
      }
    };

    /**
     * Handles an incoming conversation state update
     * @param event
     */
    const updateConversationStateHandler = async (event: any) => {
      const unassignedInquiryExistRes = await inquiryService.getUnassignedInquiries();
      if (unassignedInquiryExistRes.isErr()) {
        return await errorService.handleError(unassignedInquiryExistRes.error);
      }

      if (unassignedInquiryExistRes.value) {
        const newUnAssignedInquiryList = unassignedInquiryExistRes.value.filter(
          (id: string) => id !== event.conversation_id
        );
        newUnAssignedInquiryList.length == 0 ? (showNewIcon.value = false) : (showNewIcon.value = true);
      }
    };

    /// Mark: Events: Register event handlers for conversation repository
    conversationRepository.subscribeToConversationEvents();
    conversationRepository.updateConversationState.on(updateConversationStateHandler);

    onCreated(async () => {
      await conversationRepository.startSubscription();
    });

    watch(
      () => $route,
      (from: any, to: any) => {
        if (from && to && from.name === "conversation" && from.path !== to.path) {
          window.location.reload();
        }
      }
    );

    store.subscribe((mutation, state) => {
      switch (mutation.type) {
        case ComponentMutation.SET_NAVIGATION:
          title.value = makeTitle(mutation.payload.title);
          isGuest.value = mutation.payload.isGuest;
          isActivation.value = mutation.payload.isActivation;
          canSignUpCancel.value = mutation.payload.canSignUpCancel;
          signedUp.value = mutation.payload.signedUp;
          fromSignUpBtn.value = mutation.payload.fromSignUpBtn;
          isGroup.value = mutation.payload.isGroup;
          conversationId.value = mutation.payload.conversationId;
          if (mutation.payload.titleColor) {
            titleColor.value = `#${mutation.payload.titleColor}`;
            titleTxtStyle.value = `color: #${mutation.payload.titleColor}`;
          }
          if (mutation.payload.titleBgColor) {
            titleBgColor.value = `#${mutation.payload.titleBgColor}`;
          }
          break;
        case ComponentMutation.SET_NAVIGATION_UPDATE:
          if (mutation.payload.title != null) title.value = makeTitle(mutation.payload.title);
          if (mutation.payload.showBackBtn != null) showBackBtn.value = mutation.payload.showBackBtn;
          if (mutation.payload.canSignUpCancel != null) canSignUpCancel.value = mutation.payload.canSignUpCancel;
          break;
        case ConversationMutation.SET_SUBSCRIBED_UNREAD:
          console.log(mutation);
          break;
        case ConversationMutation.SET_SHOW_NEW_ICON:
          showNewIconHandler();
          break;
        case ConversationMutation.SET_IMPORTANCE:
          importance.value = mutation.payload;
          break;
        case ConversationMutation.SET_FORWARD_INQUIRY_ID:
          forwardInquiryId.value = mutation.payload;
          break;
      }
    });

    return {
      title,
      isGuest,
      isActivation,
      signedUp,
      fromSignUpBtn,
      btnText,
      canSignUpCancel,
      isGroup,
      forwardInquiryId,
      importance,
      showBackBtn,
      titleBgColor,
      titleColor,
      titleTxtStyle,
      fromInquiryHistory,
      dialogState,
      confirmDialog,
      shouldShowCompleteDialog,
      shouldShowResponderDialog,
      closeChatState,
      shouldShowRefuseDialog,
      showAppBar,
      showNewIcon,
      completeBySwitchingResponder,
      completeByRefusingInquiry,
      back,
      doBack,
      showConfirmationDialogs,
      cancelForward,
      openSignup,
      cancelSignUp,
      openGroupProfile,
    };
  },
});
</script>

<style scoped>
.signup_back_button {
  background-color: transparent !important;
  -webkit-margin-end: 20px;
  -webkit-padding-end: 24px !important;
}

.signup_back_text {
  font-size: small;
  font-weight: lighter;
  white-space: nowrap;
}

.btn-common {
  min-width: 80px !important;
}

.circle-blinks {
  display: inline-block;
  text-align: center;
  animation: flash 2s infinite;
}

@keyframes flash {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.inquiryBtn {
  width: 250px;
  height: 46px !important;
  font-weight: bold;
}
</style>
